import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { coinApi } from '../../../api/coin';
import Box from '../../../assets/images/asset/boxes.svg?url';
import MarketCap from '../../../assets/images/asset/MarketCap.svg?url';
import Members from '../../../assets/images/asset/relHolders.svg?url';
import Price from '../../../assets/images/brand/values.svg?url';
import { useAiOverview } from '../../../components/hooks/useAiOverview';
import Market from '../../../components/ui/Market';
import { historyPrices } from '../../../store/reducers/price';
import { getUser } from '../../../store/reducers/user';
import { convertNumber } from '../../../tools/NumberConverterTool';
import { isEmptyObject } from '../../../utils/object';
import { ethFormat } from '../../../utils/singleAssetPage/parseData';
import {
  getCardValue,
  getEthNumber,
  getFormatedNumber,
  getGrowth,
  getPrice,
} from '../../../utils/values';

const AssetMarket = ({
  asset,
  isAssetLoading,
  holders,
  isHoldersLoading,
  days,
  setDays,
  networks,
}) => {
  const dispatch = useDispatch();
  const { network, name } = useParams();
  const [tokenMarketData, setTokenMarketData] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [infoData, setInfoData] = useState(null);
  const [widgetData, setWidgetData] = useState([{}, {}, {}, {}]);
  const [dailyVolumeDataChart, setDailyVolumeDataChart] = useState(undefined);

  const user = useSelector(getUser);
  const canDisplayAi = !isEmptyObject(user) && user.ai_explanations;

  const marketAiOverview = useAiOverview({
    isReady: canDisplayAi,
    type: 'coin',
    network,
    market: true,
  });

  const { data: volume, isLoading: isVolumeLoading } =
    coinApi.useGetCoinFromMarketDataQuery({
      id: name,
      days: '30',
    });

  const {
    currentData: history,
    isLoading: isHistoryLoading,
    isFetching: isHistoryFetching,
    isSuccess,
  } = coinApi.useGetCoinFromMarketDataQuery({
    id: name,
    days,
  });

  const {
    data: reach,
    isLoading: isReachLoading,
    isFetching: isReachFetching,
  } = coinApi.useReachTokenQuery(name);

  useEffect(() => {
    if (volume && !volume.error) {
      setDailyVolumeDataChart(volume?.total_volumes);
    }
  }, [volume]);

  useEffect(() => {
    if (isHistoryLoading) {
      dispatch(historyPrices(undefined));
    }
    if (history) {
      dispatch(historyPrices(history));
    }
    if (!history && isSuccess) {
      dispatch(historyPrices(null));
    }
  }, [dispatch, history, isHistoryFetching, isHistoryLoading, isSuccess]);

  useEffect(() => {
    if (!isAssetLoading && asset) {
      setTokenMarketData({
        ath: asset?.market_data?.ath?.usd,
        atl: asset?.market_data?.atl?.usd,
        current_price: asset?.market_data?.current_price?.usd,
      });

      setPriceData({
        day: asset.market_data.price_change_percentage_24h,
        week: asset.market_data.price_change_percentage_7d,
        month: asset.market_data.price_change_percentage_30d,
        twoMonth: asset.market_data.price_change_percentage_60d,
        year: asset.market_data.price_change_percentage_1y,
      });

      setWidgetData([
        {
          title: 'Price',
          value: getPrice(asset?.market_data?.current_price?.usd),
          icon: Price,
          growth:
            asset?.market_data?.current_price?.usd &&
            asset?.market_data?.price_change_percentage_7d
              ? getGrowth(
                  asset?.market_data?.current_price?.usd,
                  (asset?.market_data?.current_price?.usd || 0) /
                    (1 +
                      (asset?.market_data?.price_change_percentage_7d || 0) /
                        100),
                  getPrice(
                    (asset?.market_data?.current_price?.usd || 0) /
                      (1 +
                        (asset?.market_data?.price_change_percentage_7d || 0) /
                          100)
                  )
                )
              : null,
        },
        {
          title: 'Market Cap',
          value: getPrice(asset?.market_data?.market_cap?.usd, true),
          icon: MarketCap,
        },
        {
          title: 'Circulating Supply',
          value: getEthNumber(asset.market_data.circulating_supply, true),
          icon: Box,
        },
        {
          title: 'Relevant Holders',
          value: getCardValue(holders?.relevant_holder_count),
          icon: Members,
          info: 'Holders owning at least 5$ worth of this token',
          isLoading: isHoldersLoading,
          growth: getGrowth(
            holders?.relevant_holder_count,
            holders?.previous_values?.relevant_holder_count,
            getFormatedNumber(holders?.previous_values?.relevant_holder_count)
          ),
        },
      ]);

      setInfoData([
        {
          title: 'Daily Volume',
          value:
            Object.keys(asset.market_data.total_volume).length &&
            asset.market_data.total_volume.usd !== null
              ? ethFormat(asset.market_data.total_volume.usd)
              : '?',
          chart: dailyVolumeDataChart,
          isLoading: isVolumeLoading,
        },
        {
          title: 'Holders',
          value:
            holders && holders.holder_count !== null
              ? convertNumber(holders.holder_count)
              : '?',
          isLoading: isHoldersLoading,
          growth: getGrowth(
            holders?.holder_count,
            holders?.previous_values?.holder_count,
            getFormatedNumber(holders?.previous_values?.holder_count)
          ),
        },
        {
          title: 'Monthly Active Addresses',
          value:
            holders && holders.monthly_active_addresses !== null
              ? convertNumber(holders.monthly_active_addresses)
              : '?',
          isLoading: isHoldersLoading,
          growth: getGrowth(
            holders?.monthly_active_addresses,
            holders?.previous_values?.monthly_active_addresses,
            getFormatedNumber(
              holders?.previous_values?.monthly_active_addresses
            )
          ),
        },
      ]);
    }
  }, [
    asset,
    dailyVolumeDataChart,
    holders,
    holders?.previous_values?.relevant_holder_count,
    holders?.relevant_holder_count,
    isAssetLoading,
    isHoldersLoading,
    isVolumeLoading,
  ]);

  return (
    <Market
      isAssetLoading={isAssetLoading}
      isHoldersLoading={isHoldersLoading}
      marketData={tokenMarketData}
      id={asset?.id}
      widgetData={widgetData}
      infoData={infoData}
      priceData={priceData}
      setDays={setDays}
      days={days}
      isLoading={isHistoryLoading}
      isFetching={isHistoryFetching}
      isReachLoading={isReachLoading || isReachFetching || isHoldersLoading}
      holders={reach?.profile_count}
      reach={reach}
      type="coin"
      symbol={asset?.symbol}
      networks={networks}
      canDisplayAi={canDisplayAi}
      name={asset?.name}
      aiOverview={marketAiOverview}
    />
  );
};

export default AssetMarket;
